/**
 * PickForm Component - Phase 2D-2
 *
 * Form for creating a prediction in Pick Mode.
 * Now uses real Markets/Outcomes from the API.
 * BSL Compliant: No coaching or sentiment indicators.
 */
import { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button } from '../common';
import { Select } from '../common';
import { StakeInput } from './StakeInput';
import { ConsequencePanel } from './ConsequencePanel';
import type { PickSlateEvent, PoolMembership, SubmitPickRequest } from '../../types';

interface PickFormProps {
  event: PickSlateEvent;
  membership: PoolMembership;
  onSubmit: (request: SubmitPickRequest) => void;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
  className?: string;
}

export function PickForm({
  event,
  membership,
  onSubmit,
  onCancel,
  loading = false,
  error = null,
  className = '',
}: PickFormProps) {
  const [selectedMarketId, setSelectedMarketId] = useState<number | null>(null);
  const [selectedOutcomeId, setSelectedOutcomeId] = useState<number | null>(null);
  const [stakeAmount, setStakeAmount] = useState<number>(0);

  // Reset form when event changes
  useEffect(() => {
    setSelectedMarketId(null);
    setSelectedOutcomeId(null);
    setStakeAmount(0);
  }, [event.event_id]);

  // Get selected market and outcome objects
  const selectedMarket = useMemo(() => {
    return event.markets.find(m => m.market_id === selectedMarketId) || null;
  }, [event.markets, selectedMarketId]);

  const selectedOutcome = useMemo(() => {
    if (!selectedMarket) return null;
    return selectedMarket.outcomes.find(o => o.outcome_id === selectedOutcomeId) || null;
  }, [selectedMarket, selectedOutcomeId]);

  // Build market options
  const marketOptions = useMemo(() => {
    const opts = [{ value: '', label: 'Select market type...' }];
    event.markets.forEach(m => {
      const label = m.line_value !== null 
        ? `${m.market_name} (${m.line_value})` 
        : m.market_name;
      opts.push({ value: m.market_id.toString(), label });
    });
    return opts;
  }, [event.markets]);

  // Build outcome options
  const outcomeOptions = useMemo(() => {
    if (!selectedMarket) return [{ value: '', label: 'Select outcome...' }];
    const opts = [{ value: '', label: 'Select outcome...' }];
    selectedMarket.outcomes.forEach(o => {
      const oddsLabel = o.odds > 0 ? `+${o.odds}` : o.odds.toString();
      opts.push({ value: o.outcome_id.toString(), label: `${o.name} (${oddsLabel})` });
    });
    return opts;
  }, [selectedMarket]);

  const handleMarketChange = (value: string) => {
    if (value === '') {
      setSelectedMarketId(null);
      setSelectedOutcomeId(null);
    } else {
      setSelectedMarketId(parseInt(value, 10));
      setSelectedOutcomeId(null);
    }
  };

  const handleOutcomeChange = (value: string) => {
    if (value === '') {
      setSelectedOutcomeId(null);
    } else {
      setSelectedOutcomeId(parseInt(value, 10));
    }
  };

  const isValid = selectedMarketId !== null && 
                  selectedOutcomeId !== null && 
                  stakeAmount > 0 && 
                  stakeAmount <= membership.current_balance;

  const handleSubmit = () => {
    if (!isValid || selectedMarketId === null || selectedOutcomeId === null) return;

    const request: SubmitPickRequest = {
      event_id: event.event_id,
      market_id: selectedMarketId,
      outcome_id: selectedOutcomeId,
      stake_amount: stakeAmount,
    };

    onSubmit(request);
  };



  return (
    <div className={`space-y-4 ${className}`}>
      {/* Event Info */}
      <Card>
        <CardHeader>
          <CardTitle>{event.event_name}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-400">
            {event.home_team} vs {event.away_team}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Lock time: {new Date(event.lock_time).toLocaleString()}
          </div>
        </CardContent>
      </Card>

      {/* Market Selection */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Market Type
            </label>
            <Select
              options={marketOptions}
              value={selectedMarketId?.toString() || ''}
              onChange={handleMarketChange}
              disabled={loading}
            />
          </div>

          {/* Outcome Selection */}
          {selectedMarketId && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Outcome
              </label>
              <Select
                options={outcomeOptions}
                value={selectedOutcomeId?.toString() || ''}
                onChange={handleOutcomeChange}
                disabled={loading}
              />
            </div>
          )}

          {/* Stake Input */}
          {selectedOutcomeId && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Stake Amount
              </label>
              <StakeInput
                value={stakeAmount}
                onChange={setStakeAmount}
                maxBalance={membership.current_balance}
                disabled={loading}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Consequence Panel */}
      {selectedOutcome && stakeAmount > 0 && (
        <ConsequencePanel
          stakeAmount={stakeAmount}
          currentBalance={membership.current_balance}
          odds={selectedOutcome?.odds}
        />
      )}

      {/* Error Display */}
      {error && (
        <Card>
          <CardContent className="p-4">
            <div className="text-red-400 text-sm">{error}</div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!isValid || loading}
        >
          {loading ? 'Submitting...' : 'Submit Prediction'}
        </Button>
      </div>
    </div>
  );
}
